"use client";

import Image from "next/image";

import { CountBadge } from "@/components/ui/CountBadge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

interface MobileFiltersSheetProps {
  children: React.ReactNode;
  activeFilterCount: number;
  onClearAll?: () => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** If true, hides the built-in trigger button (use when controlling externally) */
  hideTrigger?: boolean;
}

/**
 * Mobile filters sheet component - slides in from the left on mobile devices.
 * Wraps filter content in a Sheet with header, scrollable body, and apply button.
 */
export function MobileFiltersSheet({
  children,
  activeFilterCount,
  onClearAll,
  isOpen,
  onOpenChange,
  hideTrigger = false,
}: MobileFiltersSheetProps) {
  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      {!hideTrigger && (
        <SheetTrigger asChild>
          <button className="text-action flex items-center gap-2 rounded-full border border-black/10 px-4 py-2 transition-colors hover:bg-gray-50">
            <Image src="/icons/filters-icon.svg" alt="" width={14} height={14} />
            <span className="text-ink">Filters</span>
            <CountBadge count={activeFilterCount} />
          </button>
        </SheetTrigger>
      )}

      <SheetContent side="left" className="flex h-full w-[85vw] max-w-[320px] flex-col p-0">
        {/* Header */}
        <SheetHeader className="flex-shrink-0 border-b border-gray-100 py-4 pl-5 pr-4">
          <div className="flex items-center gap-3">
            <Image src="/icons/filters-icon.svg" alt="" width={14} height={14} />
            <SheetTitle className="text-action text-ink">Filters</SheetTitle>
            <CountBadge count={activeFilterCount} />
            {activeFilterCount > 0 && onClearAll && (
              <button
                onClick={onClearAll}
                className="ml-2 text-sm text-gray-500 underline hover:text-gray-700"
              >
                Clear all
              </button>
            )}
          </div>
        </SheetHeader>

        {/* Scrollable Filter Content - pl-5 aligns with header filter icon */}
        <div className="flex-1 overflow-y-auto pb-4 pt-2 pl-5 pr-4">{children}</div>

        {/* Footer with Apply Button */}
        <div className="flex-shrink-0 border-t border-gray-100 p-4">
          <button
            onClick={() => onOpenChange(false)}
            className="w-full rounded-full bg-black py-3 text-sm font-medium text-white transition-colors hover:bg-gray-800"
          >
            Show Results
            {activeFilterCount > 0 && ` (${activeFilterCount} filters)`}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
